<template>
  <div>
    <!-- Public mode (master) - no layout, accessed from slave -->
    <div v-if="isPublicMode" class="page-content">
      <div class="content-wrapper">
        <div class="content d-flex justify-content-center align-items-center">
          <div class="card" style="max-width: 800px; width: 100%;">
            <div class="card-body">
              <div v-if="tokenError" class="alert alert-danger">
                <strong>{{ $t('google_provisioning.error') }}:</strong>
                {{ tokenError }}
              </div>
              <template v-else>
                <div>
                  <provisioning-content
                    :is-public="true"
                    :current-provisioning="currentProvisioning"
                    :provisioning-list="provisioningList"
                    :loading="loading"
                    :checking-billing="checkingBilling"
                  />
                </div>
              </template>
            </div>
          </div>
        </div>
      </div>
    </div>
    <!-- Authenticated mode (master) or Slave mode -->
    <layout v-else>
      <page-header :header="$t('google_provisioning.title')" />
      <div class="content">
        <!-- Slave mode: Show button to open master OR show logs if already connected -->
        <div v-if="isSlaveMode">
          <!-- Show logs if provisioning is completed -->
          <div v-if="currentProvisioning && isCompletedStatus(currentProvisioning.status)" class="card">
            <div class="card-header">
              <h5 class="mb-0">Google Services Connected</h5>
            </div>
            <div class="card-body">
              <provisioning-content
                :is-public="false"
                :current-provisioning="currentProvisioning"
                :provisioning-list="provisioningList"
                :loading="loading"
                :checking-billing="checkingBilling"
              />
            </div>
          </div>
          <!-- Show iframe with master auth form if no completed provisioning -->
          <div v-else class="card">
            <div class="card-header">
              <h5 class="mb-0">Connect Google Services</h5>
            </div>
            <div class="card-body p-0">
              <div v-if="masterAuthUrl" class="master-auth-iframe-container">
                <iframe
                  :src="masterAuthUrl"
                  class="master-auth-iframe"
                  frameborder="0"
                  allow="clipboard-read; clipboard-write"
                  @load="onIframeLoad"
                ></iframe>
              </div>
              <div v-else class="text-center p-4">
                <div class="spinner-border text-primary" role="status">
                  <span class="sr-only">Loading...</span>
                </div>
                <p class="mt-3 text-muted">Preparing authorization form...</p>
              </div>
            </div>
          </div>
        </div>

        <!-- Master mode: Show clients table and connection form -->
        <template v-else>
          <!-- Clients Table -->
          <div v-if="provisioningList && provisioningList.length" class="card mb-4">
            <div class="card-header">
              <h5 class="mb-0">Connected Clients</h5>
            </div>
            <div class="card-body">
              <div class="table-responsive">
                <table class="table table-sm table-striped">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Client Domain</th>
                      <th>Status</th>
                      <th>Project ID</th>
                      <th>Created</th>
                      <th>Completed</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="item in provisioningList" :key="item.id">
                      <td>{{ item.id }}</td>
                      <td>
                        <strong v-if="item.slave_domain">{{ item.slave_domain }}</strong>
                        <span v-else class="text-muted">Master (self)</span>
                      </td>
                      <td>
                        <span :class="getStatusClass(item.status)">
                          {{ $t('google_provisioning.status_' + item.status) }}
                        </span>
                      </td>
                      <td>{{ item.google_project_id || '—' }}</td>
                      <td>{{ formatDate(item.created_at) }}</td>
                      <td>{{ item.completed_at ? formatDate(item.completed_at) : '—' }}</td>
                      <td>
                        <button
                          v-if="item.logs && item.logs.length"
                          type="button"
                          class="btn btn-sm btn-outline-info"
                          @click="showClientLogs(item)"
                        >
                          <i class="icon-eye"></i> Logs
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <!-- Connection Form - Only show in public mode (when request comes from slave) -->
          <div v-if="isPublicMode" class="card">
            <div class="card-header">
              <h5 class="mb-0">Create New Provisioning</h5>
            </div>
            <div class="card-body">
              <provisioning-content
                :is-public="true"
                :current-provisioning="currentProvisioning"
                :provisioning-list="provisioningList"
                :loading="loading"
                :checking-billing="checkingBilling"
              />
            </div>
          </div>

          <!-- Logs Modal -->
          <div
            v-if="selectedClientProvisioning"
            class="modal fade show"
            style="display: block;"
            tabindex="-1"
            @click.self="selectedClientProvisioning = null"
          >
            <div class="modal-dialog modal-lg">
              <div class="modal-content">
                <div class="modal-header">
                  <h5 class="modal-title">
                    Logs for {{ selectedClientProvisioning.slave_domain || 'Master' }}
                    <span v-if="selectedClientProvisioning.google_project_id" class="text-muted">
                      (Project: {{ selectedClientProvisioning.google_project_id }})
                    </span>
                  </h5>
                  <button type="button" class="close" @click="selectedClientProvisioning = null">
                    <span>&times;</span>
                  </button>
                </div>
                <div class="modal-body">
                  <div v-if="selectedClientProvisioning.logs && selectedClientProvisioning.logs.length" class="logs-container" style="max-height: 500px; overflow-y: auto;">
                    <div
                      v-for="log in selectedClientProvisioning.logs"
                      :key="log.id"
                      :class="'log-entry log-' + log.level"
                    >
                      <small>{{ formatDate(log.created_at) }}</small>
                      <span>{{ log.message }}</span>
                    </div>
                  </div>
                  <div v-else class="text-muted">No logs available</div>
                </div>
                <div class="modal-footer">
                  <button type="button" class="btn btn-secondary" @click="selectedClientProvisioning = null">Close</button>
                </div>
              </div>
            </div>
          </div>
          <div v-if="selectedClientProvisioning" class="modal-backdrop fade show"></div>
        </template>
      </div>
    </layout>
  </div>
</template>

<script>
import moment from 'moment'
import Api from './Api'
import ProvisioningContent from './ProvisioningContent.vue'

export default {
  name: 'GoogleProvisioning',
  components: {
    ProvisioningContent,
  },
  data() {
    return {
      loading: false,
      currentProvisioning: null,
      provisioningList: [],
      refreshInterval: null,
      useExistingProject: false,
      existingProjectId: '',
      checkingBilling: false,
      moduleConfig: null,
      isPublicMode: false,
      tokenError: null,
      returnUrl: null, // URL to return credentials to slave
      selectedClientProvisioning: null, // For showing logs modal
      currentProvisioningId: null, // Provisioning ID from callback (for public mode)
      messageHandler: null, // Message handler for iframe communication
    }
  },
  computed: {
    isSlaveMode() {
      return this.moduleConfig && this.moduleConfig.mode === 'slave'
    },
    masterAuthUrl() {
      if (!this.isSlaveMode || !this.moduleConfig || !this.moduleConfig.master_url) {
        return null
      }

      const token = this.moduleConfig.public_auth_token || ''
      if (!token) {
        return null
      }

      // Build URL to open master's public auth page
      const consolePath = '/console'
      const returnUrl = `${window.location.origin}${this.$route.path}`
      return `${this.moduleConfig.master_url}${consolePath}?token=${encodeURIComponent(token)}&return_url=${encodeURIComponent(returnUrl)}#/google_provisioning/public`
    },
  },
  async created() {
    // Check if this is public route (has token in query)
    // In hash routing, query params can be in:
    // 1. $route.query (if they're in hash part: #/path?token=...)
    // 2. window.location.search (if they're in main URL: /path?token=...#/hash)
    // 3. window.location.hash (if they're in hash: #/path?token=...)
    const query = this.$route.query
    const urlParams = new URLSearchParams(window.location.search)
    const hashParams = new URLSearchParams(window.location.hash.split('?')[1] || '')
    const token = query.token || urlParams.get('token') || hashParams.get('token')
    const returnUrl = query.return_url || urlParams.get('return_url') || hashParams.get('return_url')

    if (token) {
      this.isPublicMode = true
      this.tokenError = null // Will be set if token is invalid
      this.returnUrl = returnUrl || null
      /* eslint-disable-next-line no-console */
      console.log('Public mode enabled, isPublicMode:', this.isPublicMode)
    }

    // Load module config
    try {
      this.moduleConfig = await this.api().getConfig()
    } catch (error) {
      /* eslint-disable-next-line no-console */
      console.error('Failed to load module config:', error)
    }

    // If public mode, verify token is valid (will be done when getting auth URL)
    if (this.isPublicMode) {
      // Token will be verified when calling getAuthUrl
    }

    // Load current provisioning if not in public mode
    // In public mode (opened from slave), we don't need to load existing provisioning (requires auth)
    // In slave mode, we load provisioning to check if credentials are already received
    if (!this.isPublicMode) {
      await this.loadCurrent()

      // Auto-refresh if processing
      if (this.currentProvisioning && this.isProcessingStatus(this.currentProvisioning.status)) {
        this.startAutoRefresh()
      }

      // Check billing status on page load if provisioning is completed
      if (
        this.currentProvisioning &&
        this.isCompletedStatus(this.currentProvisioning.status) &&
        this.currentProvisioning.google_project_id &&
        this.currentProvisioning.billing_enabled === null
      ) {
        // Billing status was not checked, check it now
        this.checkBillingStatus(true) // silent check on load
      }
    } else if (this.isPublicMode) {
      // In public mode, we still want to show the connection button
      // But we don't load existing provisioning (it requires auth)
      // The button will trigger OAuth flow which will create a new provisioning
      this.currentProvisioning = null
      this.provisioningList = []
    }

    // For slave mode, ensure we have token for accessing master
    if (this.isSlaveMode && !this.moduleConfig.public_auth_token) {
      this.$toast.warning('Public auth token not configured. Please set GOOGLE_PROVISIONING_PUBLIC_AUTH_TOKEN in .env file.')
    }

    // Listen for credentials from master (iframe)
    if (this.isSlaveMode) {
      this.setupMessageListener()
    }
  },
  beforeDestroy() {
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval)
    }
    // Remove message listener
    if (this.messageHandler) {
      window.removeEventListener('message', this.messageHandler)
    }
  },
  methods: {
    api() {
      return new Api()
    },
    async loadCurrent() {
      try {
        const response = await this.api().getCurrent()
        // API returns array of provisionings (latest first)
        const list = response || []
        this.provisioningList = list
        this.currentProvisioning = list.length ? list[0] : null
      } catch (error) {
        /* eslint-disable-next-line no-console */
        console.error('Failed to load provisioning status:', error)
        // In public mode, don't show error toast (might not have auth)
        if (!this.isPublicMode) {
          this.$toast.error('Failed to load status')
        }
        this.currentProvisioning = null
      }
    },
    async connectGoogle(useExistingProject = null, existingProjectId = null) {
      this.loading = true

      try {
        // Use passed parameters or fall back to component data
        const useExisting = useExistingProject !== null ? useExistingProject : this.useExistingProject
        const existingId = existingProjectId !== null ? existingProjectId : this.existingProjectId

        // For public mode, pass token and return_url
        // Get token from $route.query, window.location.search, or hash
        let token = null
        let returnUrl = null
        if (this.isPublicMode) {
          const urlParams = new URLSearchParams(window.location.search)
          const hashParams = new URLSearchParams(window.location.hash.split('?')[1] || '')
          token = this.$route.query.token || urlParams.get('token') || hashParams.get('token')
          returnUrl = this.returnUrl || this.$route.query.return_url || urlParams.get('return_url') || hashParams.get('return_url')
        }

        const response = await this.api().getAuthUrl(token, returnUrl)

        // Check for token error
        if (response.error && response.error.includes('token')) {
          this.tokenError = response.message || 'Invalid authorization token'
          this.loading = false
          return
        }

        // AdminApi.submit возвращает уже response.data, поэтому берем auth_url напрямую
        let authUrl = response.auth_url

        // Add project_id to callback URL if using existing project
        if (useExisting && existingId) {
          // Store project_id in sessionStorage to retrieve in callback
          sessionStorage.setItem('google_provisioning_project_id', existingId)
          // Also encode in state parameter
          const url = new URL(authUrl)
          const originalState = url.searchParams.get('state')
          if (originalState) {
            try {
              // State might be JSON-encoded
              const stateData = JSON.parse(atob(originalState))
              stateData.project_id = existingId
              url.searchParams.set('state', btoa(JSON.stringify(stateData)))
            } catch (e) {
              // State is not JSON, add project_id directly
              const stateData = {
                original: originalState,
                project_id: existingId,
              }
              url.searchParams.set('state', btoa(JSON.stringify(stateData)))
            }
            authUrl = url.toString()
          }

          /* eslint-disable-next-line no-console */
          console.log('Using existing project ID:', existingId)
        } else {
          sessionStorage.removeItem('google_provisioning_project_id')
        }

        // Open OAuth window
        const width = 600
        const height = 700
        const left = (window.screen.width - width) / 2
        const top = (window.screen.height - height) / 2

        const popup = window.open(
          authUrl,
          'Google OAuth',
          `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`
        )

        // Listen for messages from popup
        const messageHandler = (event) => {
          if (event.data.type === 'google_oauth_success') {
            window.removeEventListener('message', messageHandler)
            this.loading = false

            // If public mode and has return_url, we need to wait for provisioning to complete
            // and then send credentials back to slave
            if (this.isPublicMode && this.returnUrl) {
              // Store provisioning_id from callback if available
              if (event.data.provisioning_id) {
                this.currentProvisioningId = event.data.provisioning_id
              }
              // Start auto-refresh to monitor provisioning status
              // In public mode, we can't use loadCurrent() (requires auth), so we'll use provisioning_id
              this.startAutoRefresh()
              // When provisioning completes, sendCredentialsToSlave will be called from refreshStatus
            } else {
              // Normal mode - just refresh and show success
              this.$toast.success(event.data.message || 'Authorization successful')
              this.loadCurrent()
              this.startAutoRefresh()
            }
          } else if (event.data.type === 'google_oauth_error') {
            window.removeEventListener('message', messageHandler)
            this.loading = false
            this.$toast.error(event.data.error || 'Authorization failed')
          }
        }

        window.addEventListener('message', messageHandler)

        // Fallback: check if popup was closed manually
        const checkClosed = setInterval(() => {
          if (popup.closed) {
            clearInterval(checkClosed)
            window.removeEventListener('message', messageHandler)
            if (this.loading) {
              this.loading = false
              this.$toast.warning('Authorization window was closed')
            }
          }
        }, 1000)
      } catch (error) {
        /* eslint-disable-next-line no-console */
        console.error('Failed to get auth URL:', error)
        if (error.response && error.response.data && error.response.data.error) {
          this.tokenError = error.response.data.message || 'Authorization failed'
        } else {
          this.$toast.error('Failed to initiate Google connection')
        }
        this.loading = false
      }
    },
    setupMessageListener() {
      // Listen for credentials from master (iframe)
      this.messageHandler = (event) => {
        // Verify origin matches master URL
        if (!this.moduleConfig || !this.moduleConfig.master_url) {
          return
        }

        const masterOrigin = new URL(this.moduleConfig.master_url).origin
        if (event.origin !== masterOrigin) {
          return // Ignore messages from other origins
        }

        if (event.data.type === 'google_provisioning_credentials') {
          this.loading = false
          this.$toast.success('Credentials received successfully')
          // Save credentials to local database
          this.saveCredentials(event.data.credentials)
        }
      }

      window.addEventListener('message', this.messageHandler)
    },
    onIframeLoad() {
      // Iframe loaded, can add any loading state handling here if needed
      this.loading = false
    },
    async sendCredentialsToSlave(provisioning) {
      if (!this.returnUrl) {
        return
      }

      try {
        // Send credentials to slave via postMessage to the return URL's origin
        const returnUrlObj = new URL(this.returnUrl)

        // Get full provisioning data (temporarily saved in master DB)
        // In public mode, use provisioning_id to load via public endpoint
        let fullProvisioning = provisioning
        if (this.isPublicMode && this.currentProvisioningId) {
          try {
            const urlParams = new URLSearchParams(window.location.search)
            const hashParams = new URLSearchParams(window.location.hash.split('?')[1] || '')
            const token = this.$route.query.token || urlParams.get('token') || hashParams.get('token')

            fullProvisioning = await this.api().getProvisioningPublic(this.currentProvisioningId, token)
          } catch (error) {
            /* eslint-disable-next-line no-console */
            console.error('Failed to load provisioning in public mode:', error)
            // Fall back to provided provisioning
            fullProvisioning = provisioning
          }
        } else if (!this.isPublicMode) {
          // Normal mode - use loadCurrent()
          await this.loadCurrent()
          fullProvisioning = this.currentProvisioning
        }

        // Ensure logs are included in credentials (they should be loaded via with('logs') in API)
        if (!fullProvisioning || !fullProvisioning.logs) {
          /* eslint-disable-next-line no-console */
          console.warn('Provisioning data does not include logs, trying to reload...')
          // Try to reload with explicit logs loading
          if (this.isPublicMode && this.currentProvisioningId) {
            const urlParams = new URLSearchParams(window.location.search)
            const hashParams = new URLSearchParams(window.location.hash.split('?')[1] || '')
            const token = this.$route.query.token || urlParams.get('token') || hashParams.get('token')
            const provisioningWithLogs = await this.api().getProvisioningPublic(this.currentProvisioningId, token)
            if (provisioningWithLogs && provisioningWithLogs.logs) {
              fullProvisioning.logs = provisioningWithLogs.logs
            }
          } else {
            const provisioningWithLogs = await this.api().getProvisioning(fullProvisioning.id)
            if (provisioningWithLogs && provisioningWithLogs.logs) {
              fullProvisioning.logs = provisioningWithLogs.logs
            }
          }
        }

        // Send message to parent window (slave) - can be either window.opener (popup) or window.parent (iframe)
        const targetWindow = window.opener || window.parent
        if (targetWindow && targetWindow !== window) {
          targetWindow.postMessage(
            {
              type: 'google_provisioning_credentials',
              credentials: fullProvisioning, // This includes logs if they were loaded
            },
            returnUrlObj.origin
          )

          // After sending to slave, clear credentials from master DB (they're now on slave)
          // Only keep tracking info (status, logs, project_id, slave_domain)
          try {
            await this.clearCredentialsFromMaster(fullProvisioning.id)
          } catch (error) {
            /* eslint-disable-next-line no-console */
            console.error('Failed to clear credentials from master:', error)
            // Don't fail the whole process if clearing fails
          }

          // Close popup if it was opened in popup (not iframe)
          if (window.opener) {
            setTimeout(() => {
              window.close()
            }, 1000)
          }
        }
      } catch (error) {
        /* eslint-disable-next-line no-console */
        console.error('Failed to send credentials to slave:', error)
      }
    },
    async clearCredentialsFromMaster(provisioningId) {
      try {
        // Clear sensitive credentials from master DB, keep only tracking info
        await this.api().clearCredentials(provisioningId)
      } catch (error) {
        /* eslint-disable-next-line no-console */
        console.error('Failed to clear credentials from master:', error)
        throw error
      }
    },
    async saveCredentials(credentials) {
      try {
        // Wrap credentials in the expected format for the API
        await this.api().receiveCredentials({ credentials })
        this.$toast.success('Credentials saved successfully')
        await this.loadCurrent()
      } catch (error) {
        /* eslint-disable-next-line no-console */
        console.error('Failed to save credentials:', error)
        this.$toast.error('Failed to save credentials')
      }
    },
    isProcessingStatus(status) {
      return status && ['authenticating', 'creating_project', 'enabling_apis', 'creating_credentials'].includes(status)
    },
    isCompletedStatus(status) {
      return status === 'completed'
    },
    isFailedStatus(status) {
      return status === 'failed'
    },
    async refreshStatus() {
      // In public mode, use provisioning_id to load data via public endpoint
      if (this.isPublicMode && this.currentProvisioningId) {
        try {
          const urlParams = new URLSearchParams(window.location.search)
          const hashParams = new URLSearchParams(window.location.hash.split('?')[1] || '')
          const token = this.$route.query.token || urlParams.get('token') || hashParams.get('token')

          const provisioning = await this.api().getProvisioningPublic(this.currentProvisioningId, token)
          if (provisioning) {
            this.currentProvisioning = provisioning
          }
        } catch (error) {
          /* eslint-disable-next-line no-console */
          console.error('Failed to load provisioning in public mode:', error)
        }
      } else if (!this.isPublicMode) {
        // Normal mode - use loadCurrent()
        await this.loadCurrent()
      }

      // Stop auto-refresh if completed or failed
      if (
        this.currentProvisioning &&
        !this.isProcessingStatus(this.currentProvisioning.status)
      ) {
        this.stopAutoRefresh()
      }

      // If completed and in public mode with return_url, send credentials
      if (
        this.isPublicMode &&
        this.returnUrl &&
        this.currentProvisioning &&
        this.isCompletedStatus(this.currentProvisioning.status)
      ) {
        this.sendCredentialsToSlave(this.currentProvisioning)
      }
    },
    startAutoRefresh() {
      if (this.refreshInterval) {
        clearInterval(this.refreshInterval)
      }

      this.refreshInterval = setInterval(() => {
        this.refreshStatus()
      }, 5000) // Refresh every 5 seconds
    },
    stopAutoRefresh() {
      if (this.refreshInterval) {
        clearInterval(this.refreshInterval)
        this.refreshInterval = null
      }
    },
    formatDate(date) {
      return moment(date).format('DD.MM.YYYY HH:mm:ss')
    },
    getStatusClass(status) {
      const classes = {
        pending: 'badge badge-warning',
        authenticating: 'badge badge-info',
        creating_project: 'badge badge-info',
        enabling_apis: 'badge badge-info',
        creating_credentials: 'badge badge-info',
        completed: 'badge badge-success',
        failed: 'badge badge-danger',
      }
      return classes[status] || 'badge badge-secondary'
    },
    async checkBillingStatus(silent = false) {
      if (!this.currentProvisioning || !this.currentProvisioning.id) {
        return
      }

      this.checkingBilling = true

      try {
        const response = await this.api().checkBilling(this.currentProvisioning.id)

        if (response.success) {
          // Update current provisioning with new billing status
          this.currentProvisioning.billing_enabled = response.billing_enabled
          this.currentProvisioning.billing_account_name = response.billing_account_name

          if (!silent) {
            if (response.billing_enabled) {
              this.$toast.success(this.$t('google_provisioning.billing_enabled_success'))
            } else {
              this.$toast.warning(this.$t('google_provisioning.billing_still_disabled'))
            }
          }
        } else {
          if (!silent) {
            this.$toast.error(response.error || 'Failed to check billing status')
          }
        }
      } catch (error) {
        /* eslint-disable-next-line no-console */
        console.error('Failed to check billing status:', error)
        if (!silent) {
          this.$toast.error('Failed to check billing status')
        }
      } finally {
        this.checkingBilling = false
      }
    },
    getBillingUrl(projectId) {
      if (!projectId) {
        return 'https://console.cloud.google.com/billing'
      }
      return `https://console.cloud.google.com/billing/linkedaccount?project=${projectId}`
    },
    showClientLogs(provisioning) {
      this.selectedClientProvisioning = provisioning
    },
  },
}
</script>
<style scoped>
.logs-container {
  border: 1px solid #dadada;
  padding: 10px 15px;
  border-radius: 4px;
  background-color: #f8f9fa;
}

.log-entry {
  padding: 8px;
  margin-bottom: 5px;
  border-radius: 4px;
  border-left: 3px solid #ddd;
}

.log-entry.log-info {
  background-color: #e3f2fd;
  border-left-color: #2196f3;
}

.log-entry.log-success {
  background-color: #e8f5e9;
  border-left-color: #4caf50;
}

.log-entry.log-warning {
  background-color: #fff3e0;
  border-left-color: #ff9800;
}

.log-entry.log-error {
  background-color: #ffebee;
  border-left-color: #f44336;
}

.log-entry small {
  color: #666;
  margin-right: 10px;
  font-weight: 500;
}

code {
  background-color: #f4f4f4;
  padding: 2px 6px;
  border-radius: 3px;
  font-size: 0.9em;
}

.master-auth-iframe-container {
  position: relative;
  width: 100%;
  min-height: 600px;
  overflow: hidden;
}

.master-auth-iframe {
  width: 100%;
  height: 100%;
  min-height: 600px;
  border: none;
  display: block;
}
</style>
